"use client";

import { useEffect, useState, type CSSProperties } from "react";
import { useTheme } from "next-themes";

import { Footer, Nav } from "@/app/_components/sections";
import { InstallSnippet } from "@/components/photon/install-snippet";
import {
  benchmarkEnvironment,
  isHeadlineSpeedup,
  runtimeName,
  throughputSummary,
  workloadDescription,
  type BenchmarkDataset,
} from "@/lib/photon/display";
import { PhotonPageMark } from "./photon-wordmark";

const CONTACT_URL = "mailto:contact@moondream.ai";
const MATCHED_PAIR_WIN_THRESHOLD = 0.8;

const formatPercent = (value: number) => `${Math.round(value)}%`;

const formatThroughput = (value: number, unit: string) =>
  `${value < 10 ? value.toFixed(2) : value.toFixed(1)} ${unit}`;

const formatLatency = (value: number) =>
  value < 1000
    ? `${Math.round(value)} ms`
    : `${(value / 1000).toFixed(value >= 10000 ? 1 : 2)} s`;

const downloadJson = (filename: string, value: unknown) => {
  const url = URL.createObjectURL(
    new Blob([JSON.stringify(value, null, 2)], {
      type: "application/json;charset=utf-8",
    }),
  );
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
};

function MethodologyDetails({ benchmark }: { benchmark: BenchmarkDataset }) {
  const environment = benchmarkEnvironment(benchmark);
  return (
    <details className="perf-details photon-perf-details">
      <summary className="mono">Methodology and disclosures</summary>
      <dl className="photon-perf-method-grid">
        <div>
          <dt>Hardware</dt>
          <dd>{environment.gpu}</dd>
        </div>
        <div>
          <dt>Workload</dt>
          <dd>{environment.workload}</dd>
        </div>
        <div>
          <dt>Concurrent requests</dt>
          <dd>{environment.concurrency.join(", ")}</dd>
        </div>
        <div>
          <dt>Runtimes</dt>
          <dd>{environment.runtimes.join(" · ")}</dd>
        </div>
        {environment.date ? (
          <div>
            <dt>Updated</dt>
            <dd>{environment.date}</dd>
          </div>
        ) : null}
        {benchmark.benchmark === "describe" ? (
          <div>
            <dt>Sampling</dt>
            <dd>Varied 512×512 images; 256 output tokens per request.</dd>
          </div>
        ) : null}
      </dl>
      <p className="photon-perf-method-note">
        {benchmark.benchmark === "describe"
          ? "Describe uses varied 512×512 ChartQA images, the same description prompt, and 256 output tokens per request. Throughput is output tokens divided by measured wall time."
          : benchmark.benchmark === "librispeech"
            ? "LibriSpeech measures transcription throughput as seconds of input audio divided by measured wall time."
            : "ChartQA throughput is completed requests divided by measured wall time."}{" "}
        Latency is client-observed request duration, from sending the request to
        receiving the complete response.
        Both latency percentiles use concurrency 1. Comparisons match the
        benchmark, model, GPU, and concurrency. Missing measurements are not
        plotted.
      </p>
    </details>
  );
}

function PerformanceCta() {
  return (
    <section className="section final-cta photon-perf-final">
      <div className="container">
        <div className="final-card perf-final-card card">
          <div className="final-inner">
            <div>
              <div
                className="t-eyebrow"
                style={{ color: "var(--text-tertiary)" }}
              >
                Your workload
              </div>
              <h2 className="t-heading-xl" style={{ marginTop: 10 }}>
                Test Photon on your stack.
              </h2>
              <p
                className="t-body-lg"
                style={{
                  color: "var(--text-secondary)",
                  marginTop: 12,
                  maxWidth: 620,
                }}
              >
                Start with the published package, or talk to us about your
                model, hardware, and latency target.
              </p>
              <div style={{ marginTop: 24 }}>
                <InstallSnippet />
              </div>
            </div>
            <div className="final-ctas">
              <a href={CONTACT_URL} className="btn btn-secondary">
                Talk to us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

type BarRow = {
  runtime: string;
  value: number;
  label: string;
};

function BarChart({
  groups,
}: {
  groups: { label: string; rows: BarRow[] }[];
}) {
  return (
    <figure className="photon-perf-bar-chart">
      <div className="photon-perf-bar-groups">
        {groups.map((group) => {
          const maximum = Math.max(
            ...group.rows.map((row) => row.value),
            0.000001,
          );
          return (
            <div className="photon-perf-bar-group" key={group.label}>
              <h4 className="mono">{group.label}</h4>
              <div className="photon-perf-bar-rows">
                {group.rows.length > 0 ? (
                  group.rows.map((row) => (
                    <div className="photon-perf-bar-row" key={row.runtime}>
                      <span className="mono">{row.runtime}</span>
                      <div className="photon-perf-bar-track" aria-hidden="true">
                        <i
                          className={
                            row.runtime === "Photon" ? "is-photon" : ""
                          }
                          style={
                            {
                              "--photon-perf-width": `${Math.max(2, (row.value / maximum) * 100)}%`,
                            } as CSSProperties
                          }
                        />
                      </div>
                      <strong className="mono tabular">{row.label}</strong>
                    </div>
                  ))
                ) : (
                  <p className="photon-perf-bar-empty mono">Not measured</p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </figure>
  );
}

const throughputRowsFor = (
  benchmark: BenchmarkDataset,
  modelId: string,
  concurrency: number,
): BarRow[] =>
  benchmark.measurements
    .filter(
      (row) =>
        row.modelId === modelId &&
        row.concurrency === concurrency &&
        row.throughput !== undefined,
    )
    .sort(
      (a, b) => Number(b.engine === "photon") - Number(a.engine === "photon"),
    )
    .map((row) => ({
      runtime: runtimeName(row.engine),
      value: row.throughput!,
      label: formatThroughput(row.throughput!, benchmark.unit),
    }));

const latencyRowsFor = (
  benchmark: BenchmarkDataset,
  modelId: string,
  percentile: "p50" | "p99",
): BarRow[] =>
  benchmark.measurements
    .filter(
      (row) =>
        row.modelId === modelId &&
        row.concurrency === 1 &&
        row.latency?.[percentile] != null,
    )
    .sort(
      (a, b) => Number(b.engine === "photon") - Number(a.engine === "photon"),
    )
    .map((row) => ({
      runtime: runtimeName(row.engine),
      value: row.latency![percentile]!,
      label: formatLatency(row.latency![percentile]!),
    }));

function CorePerformanceGraphs({
  benchmark,
  modelId,
}: {
  benchmark: BenchmarkDataset;
  modelId: string;
}) {
  return (
    <BarChart
      groups={[
        {
          label: "1 request at a time",
          rows: throughputRowsFor(benchmark, modelId, 1),
        },
        {
          label: "8 requests at once",
          rows: throughputRowsFor(benchmark, modelId, 8),
        },
        {
          label: "p50",
          rows: latencyRowsFor(benchmark, modelId, "p50"),
        },
        {
          label: "p99",
          rows: latencyRowsFor(benchmark, modelId, "p99"),
        },
      ]}
    />
  );
}

export function PerformancePage({
  benchmarks,
}: {
  benchmarks: BenchmarkDataset[];
}) {
  const { resolvedTheme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [workload, setWorkload] = useState("describe");
  const [chip, setChip] = useState("H100");

  useEffect(() => setMounted(true), []);

  const theme: "dark" | "light" =
    mounted && resolvedTheme === "dark" ? "dark" : "light";
  const workloads = [...new Set(benchmarks.map((row) => row.benchmark))].sort();
  const selectedWorkload = workloads.includes(
    workload as BenchmarkDataset["benchmark"],
  )
    ? workload
    : workloads[0];
  const matching = benchmarks.filter(
    (row) => row.benchmark === selectedWorkload,
  );
  const benchmark = matching.find((row) => row.device === chip) ?? matching[0];
  const summary = benchmark ? throughputSummary([benchmark]) : null;
  const availableChips = matching.map((row) => row.device);

  return (
    <div className="md-landing photon-perf-page">
      <Nav
        theme={theme}
        onToggleTheme={() => setTheme(theme === "dark" ? "light" : "dark")}
      />

      <main>
        <section className="section photon-perf-hero">
          <div className="container">
            <PhotonPageMark section="Performance" />
            <div className="photon-perf-hero-row">
              <div>
                <h1 className="t-display-md">Photon performance.</h1>
                <p className="t-body-lg">
                  Every model with the four core performance views on one row.
                </p>
              </div>
            </div>
          </div>
        </section>

        {benchmark ? <section className="section photon-perf-section">
          <div className="container">
            {summary ? <div className="photon-perf-hybrid-summary">
              <div>
                <p className="t-eyebrow">
                  {benchmark.device} · {benchmark.modality === "vision"
                    ? "Vision-language"
                    : "Speech recognition"}
                </p>
                <h2 className="t-display-sm">
                  {isHeadlineSpeedup(summary.median)
                    ? `${formatPercent((summary.median - 1) * 100)} higher median throughput.`
                    : "Built for fast inference."}
                </h2>
                {summary.wins / summary.count >= MATCHED_PAIR_WIN_THRESHOLD ? (
                  <p className="t-body-lg">
                    Photon led {summary.wins} of {summary.count} matched
                    throughput comparisons.
                  </p>
                ) : null}
              </div>
              <dl className="photon-perf-hybrid-facts">
                <div>
                  <dt>Models</dt>
                  <dd>{benchmark.models.length}</dd>
                </div>
                {isHeadlineSpeedup(summary.max) ? (
                  <div>
                    <dt>Speedup</dt>
                    <dd>{`Up to ${Number(summary.max.toFixed(2))} times faster`}</dd>
                  </div>
                ) : null}
                <div>
                  <dt>Workload</dt>
                  <dd>{workloadDescription(benchmark.benchmark)}</dd>
                </div>
              </dl>
            </div> : (
              <p>No matched throughput comparisons available yet.</p>
            )}

            <div className="photon-perf-explorer">
              <div className="photon-perf-explorer-controls">
                <div className="filter-field">
                  <span className="filter-label mono">Workload</span>
                  <div className="seg" role="group" aria-label="Workload">
                    {workloads.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={
                          selectedWorkload === option ? "is-active" : ""
                        }
                        onClick={() => setWorkload(option)}
                      >
                        {option === "describe"
                          ? "Vision"
                          : option === "librispeech"
                            ? "Speech"
                            : "ChartQA"}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="filter-field">
                  <span className="filter-label mono">GPU</span>
                  <div className="seg" role="group" aria-label="GPU">
                    {availableChips.map((option) => (
                      <button
                        key={option}
                        type="button"
                        className={
                          benchmark.device === option ? "is-active" : ""
                        }
                        onClick={() => setChip(option)}
                      >
                        {option}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="photon-perf-chart-panel">
                <div className="photon-perf-chart-title">
                  <div>
                    <p className="t-eyebrow">
                      All {benchmark.models.length}{" "}
                      {benchmark.modality === "vision" ? "vision" : "ASR"}{" "}
                      models
                    </p>
                    <h3 className="t-heading-lg">Throughput and latency</h3>
                  </div>
                  <span className="mono">{benchmark.device}</span>
                </div>

                <div className="photon-perf-four-up-columns">
                  <span
                    className="photon-perf-four-up-column-spacer"
                    aria-hidden="true"
                  />
                  <div className="photon-perf-four-up-metric-groups">
                    <div className="photon-perf-four-up-metric-group">
                      <strong>
                        Throughput ({benchmark.unit === "tok/s"
                          ? "output tok/s"
                          : benchmark.unit})
                      </strong>
                      <span className="mono">Longer is better</span>
                    </div>
                    <div className="photon-perf-four-up-metric-group">
                      <strong>Latency (request duration)</strong>
                      <span className="mono">Shorter is better</span>
                    </div>
                  </div>
                </div>

                <div className="photon-perf-model-results">
                  {benchmark.models.map((model, index) => (
                    <section className="photon-perf-model-result" key={model.id}>
                      <header className="photon-perf-model-result-head">
                        <span className="mono">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <div>
                          <h4>{model.model}</h4>
                          {model.family ? <p>{model.family}</p> : null}
                        </div>
                      </header>
                      <CorePerformanceGraphs
                        benchmark={benchmark}
                        modelId={model.id}
                      />
                    </section>
                  ))}
                </div>
              </div>
            </div>

            <div className="photon-perf-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() =>
                  downloadJson(`photon-${benchmark.id}.json`, benchmark)
                }
              >
                Download selected dataset
              </button>
            </div>
            <MethodologyDetails benchmark={benchmark} />
          </div>
        </section> : (
          <section className="section photon-perf-section">
            <div className="container">
              <p>
                Benchmark results are not available yet. Please check back after
                the first Planck refresh.
              </p>
            </div>
          </section>
        )}

        <PerformanceCta />
      </main>

      <Footer />
    </div>
  );
}
